import { useSnapshotDrc } from "@features/circuits";
import DraftIcon from "@mui/icons-material/EditNoteRounded";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDraftStore } from "@stores/draftStore";
import { useEffect, useRef, useState } from "react";

import { RackSegment } from "./RackSegment";

/**
 * Active-draft segment — the "what's the agent currently building?"
 * readout that survives navigation. Today's `SnapshotPill` only lives in
 * a chat bubble; once the user scrolls past it the snapshot is invisible.
 * The rack solves that: as long as a snapshot is active in `draftStore`,
 * the rack carries it.
 *
 * Behavioural details lifted from the UX/UI report (§5/W3):
 *
 *   * Click → opens the DraftPanel (delegates to draftStore.openPanel).
 *   * The DRC pulse (signature motion #2) fires once when the error
 *     count goes up while the panel is closed — a 600ms ring expands
 *     from this segment's icon. Watching for `error_count` rising-edges
 *     is the trigger; we don't pulse on initial load or on parity.
 */
export function DraftSegment() {
  const { activeSnapshotId, openPanel, panelOpen } = useDraftStore();
  const { data: drc } = useSnapshotDrc(activeSnapshotId);

  // Pulse once when error_count rises and the panel is closed. We track
  // the previously-seen value via a ref so React-Query refetches don't
  // re-trigger when the count is stable.
  const prevErrors = useRef<number | null>(null);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const next = drc?.error_count ?? null;
    if (next == null) return;
    if (prevErrors.current != null && next > prevErrors.current && !panelOpen) {
      setPulse(true);
      const id = window.setTimeout(() => setPulse(false), 700);
      return () => window.clearTimeout(id);
    }
    prevErrors.current = next;
  }, [drc?.error_count, panelOpen]);

  if (!activeSnapshotId) {
    return (
      <RackSegment label="DRAFT" width={240} empty>
        <Typography variant="caption" color="text.secondary">
          No active iteration
        </Typography>
      </RackSegment>
    );
  }

  const errCount = drc?.error_count ?? 0;
  const warnCount = drc?.warning_count ?? 0;
  const drcKind: "error" | "warning" | "success" =
    errCount > 0 ? "error" : warnCount > 0 ? "warning" : "success";
  const drcLabel =
    errCount > 0
      ? `${errCount} error${errCount === 1 ? "" : "s"}`
      : warnCount > 0
        ? `${warnCount} warning${warnCount === 1 ? "" : "s"}`
        : "DRC clean";

  return (
    <RackSegment
      label="DRAFT"
      width={240}
      onClick={() => openPanel(activeSnapshotId)}
    >
      <Stack
        direction="row"
        spacing={0.875}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <DraftIcon sx={{ color: "primary.light", fontSize: 14 }} />
          {pulse && (
            <Box
              aria-hidden
              sx={(t) => ({
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: `1px solid ${t.palette.error.main}`,
                animation: `drcPulseRing ${t.motion.pulse} forwards`,
                pointerEvents: "none",
              })}
            />
          )}
        </Box>
        <Tooltip title={`Snapshot ${activeSnapshotId}`}>
          <Typography
            sx={{
              fontFamily: (t) => t.fonts.mono,
              fontSize: 11,
              fontWeight: 600,
              color: "text.primary",
              letterSpacing: "0.02em",
            }}
          >
            {activeSnapshotId.slice(0, 8)}
          </Typography>
        </Tooltip>
        <DrcReadout kind={drcKind} label={drcLabel} />
      </Stack>
    </RackSegment>
  );
}

/** DRC count chip — hairline rim + matching tone, mono caps. Replaces
 *  the bordered chip with the same instrument-rim treatment used by
 *  the project card's selected state. */
function DrcReadout({
  kind,
  label,
}: {
  kind: "error" | "warning" | "success";
  label: string;
}) {
  return (
    <Box
      sx={(t) => {
        const tone = t.palette[kind].main;
        return {
          ml: 0.25,
          px: 0.75,
          py: 0.125,
          borderRadius: 0.75,
          color: tone,
          fontSize: 10,
          fontWeight: 600,
          fontFamily: t.fonts.mono,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          bgcolor: alpha(tone, 0.1),
          boxShadow: `inset 0 0 0 1px ${alpha(tone, 0.3)}`,
        };
      }}
    >
      {label}
    </Box>
  );
}
